import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import type { GHLContact } from '~entities/parent/api/search-ghl-contacts';
import { useGhlSearchContacts } from '~entities/parent/api/search-ghl-contacts';

import type { ParentFormValues } from '~features/contacts/parent/parent-schema';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';

// Create a new interface that extends ParentFormValues
interface ExtendedParentFormValues extends ParentFormValues {
  contactId?: string;
}

interface ContactSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactSelect: (data: Partial<ExtendedParentFormValues>) => void;
}

export const ContactSearchDialog = ({
  open,
  onOpenChange,
  onContactSelect,
}: ContactSearchDialogProps) => {
  const [searchTerm, setSearchTerm] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const resetSearch = useCallback(() => {
    setSearchTerm({
      firstName: '',
      lastName: '',
      email: '',
    });
  }, []);

  // Handle dialog close
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetSearch();
    }
    onOpenChange(isOpen);
  };

  const isValidSearch =
    searchTerm.firstName.length >= 3 ||
    searchTerm.lastName.length >= 3 ||
    searchTerm.email.length >= 3;

  const { data: contacts, isLoading } = useGhlSearchContacts(
    searchTerm.firstName,
    searchTerm.lastName,
    searchTerm.email,
    isValidSearch,
  );

  const handleSearch = (
    field: 'firstName' | 'lastName' | 'email',
    value: string,
  ) => {
    setSearchTerm((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (value.length > 0 && value.length < 3) {
      toast.info('Please enter at least 3 characters to search');
    }
  };

  const handleContactSelect = (contact: GHLContact) => {
    const formattedData: Partial<ExtendedParentFormValues> = {
      contactId: contact.id,
      firstName: contact.firstNameLowerCase
        ? contact.firstNameLowerCase.charAt(0).toUpperCase() +
          contact.firstNameLowerCase.slice(1)
        : '',
      lastName: contact.lastNameLowerCase
        ? contact.lastNameLowerCase.charAt(0).toUpperCase() +
          contact.lastNameLowerCase.slice(1)
        : '',
      email: contact.email ? contact.email : '',
      primaryPhone: contact.phone,
      address: {
        city: contact.city || '',
        state: contact.state || '',
        zip: contact.postalCode || '',
        country: contact.country || '',
        street: contact.address || '',
      },
    };
    onContactSelect(formattedData);
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>Select Contact</DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='flex gap-2'>
            <Input
              placeholder='First Name (min. 3 characters)'
              value={searchTerm.firstName}
              onChange={(e) => {
                handleSearch('firstName', e.target.value);
              }}
            />
            <Input
              placeholder='Last Name (min. 3 characters)'
              value={searchTerm.lastName}
              onChange={(e) => {
                handleSearch('lastName', e.target.value);
              }}
            />
            <Input
              placeholder='Email (min. 3 characters)'
              value={searchTerm.email}
              onChange={(e) => {
                handleSearch('email', e.target.value);
              }}
            />
          </div>

          <div className='max-h-[400px] space-y-2 overflow-y-auto'>
            {!isValidSearch ? (
              <div className='py-4 text-center text-muted-foreground'>
                Enter at least 3 characters in either field to search
              </div>
            ) : isLoading ? (
              <div className='py-4 text-center'>Loading contacts...</div>
            ) : contacts && contacts.length > 0 ? (
              contacts.map((contact) => (
                <Button
                  key={contact.id}
                  variant='outline'
                  className='h-auto w-full justify-start p-3 text-left hover:bg-accent'
                  onClick={() => {
                    handleContactSelect(contact);
                  }}
                >
                  <div className='flex w-full flex-col gap-0.5'>
                    <div className='font-medium'>
                      {contact.firstNameLowerCase ?? ''}{' '}
                      {contact.lastNameLowerCase ?? ''}
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      {contact.email ?? ''}{' '}
                      {contact.email && contact.phone && ' | '}
                      {contact.phoneLabel && 'Home: '}
                      {contact.phone}
                    </div>
                  </div>
                </Button>
              ))
            ) : (
              <div className='py-4 text-center text-muted-foreground'>
                No contacts found
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
